"use server";

import { prisma } from "@/lib/prisma";
import { auth } from "@/auth";
import { getAccess } from "@/lib/access";
import { can } from "@/lib/access-rules";
import { notify } from "@/lib/notify";
import { actionKey } from "@/lib/notification-action-kinds";
import { resolveAction } from "@/lib/notification-actions";
import { CACHE_TAGS } from "@/lib/cache";
import { defineAction } from "@/lib/define-action";
import { deleteArticle } from "@/lib/actions/article-lifecycle";
import { deleteApplication, deleteClient } from "@/lib/actions/catalog";
import { deleteFeedback } from "@/lib/actions/feedback";
import { deleteTicket } from "@/lib/ticket-actions";
import { deleteEscalation } from "@/lib/escalations/lifecycle";

/** Loads a pending request; only a reviewer or the entity's owner may decide it. */
const reviewGuard = async (id: number) => {
  const session = await auth();
  const userId = session?.user?.userId;
  if (!userId) throw new Error("Not signed in");
  const request = await prisma.deletionRequest.findUnique({ where: { id } });
  if (!request) throw new Error("Deletion request not found");
  if (request.status !== "PENDING") throw new Error("Request is not pending");
  const access = await getAccess(userId);
  if (!access || (!can(access, "deletion_requests:review") && request.ownerId !== userId)) throw new Error("Not authorized");
  return { userId, request };
};

async function decide(id: number, userId: number, status: "APPROVED" | "DISMISSED") {
  await prisma.deletionRequest.update({ where: { id }, data: { status, decidedBy: userId, decidedAt: new Date() } });
  await resolveAction(actionKey("deletion_request", id), userId);
}

async function tellRequester(request: { requestedBy: number; entityLabel: string }, type: string, verb: string) {
  const requester = await prisma.user.findUnique({ where: { id: request.requestedBy }, select: { id: true, email: true, name: true } });
  if (requester) {
    await notify({ type, category: "knowledge", recipients: [requester], message: `Your deletion request for ${request.entityLabel} was ${verb}`, emailPolicy: "never", inApp: true });
  }
}

export const approveDeletionRequest = defineAction({
  guard: reviewGuard,
  run: async ({ userId, request }, id: number): Promise<{ ok: true } | { ok: false; error: string }> => {
    let result: { ok: true } | { ok: false; error: string } = { ok: true };
    switch (request.entityType) {
      case "ARTICLE": await deleteArticle(request.entityId); break;
      case "APPLICATION": result = await deleteApplication(request.entityId); break;
      case "CLIENT": result = await deleteClient(request.entityId); break;
      case "FEEDBACK": await deleteFeedback(request.entityId); break;
      case "SUPPORT_TICKET": await deleteTicket(request.entityId); break;
      case "ESCALATION": result = await deleteEscalation(request.entityId); break;
    }
    if (!result.ok) return result;
    await decide(id, userId, "APPROVED");
    await tellRequester(request, "deletion_request_approved", "approved");
    return { ok: true };
  },
  tag: CACHE_TAGS.pendingDeletions,
  paths: ["/deletion-requests"],
});

export const dismissDeletionRequest = defineAction({
  guard: reviewGuard,
  run: async ({ userId, request }, id: number) => {
    await decide(id, userId, "DISMISSED");
    await tellRequester(request, "deletion_request_dismissed", "dismissed");
    return { ok: true } as const;
  },
  tag: CACHE_TAGS.pendingDeletions,
  paths: ["/deletion-requests"],
});
